import { utils } from '@/utils';
import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import { EndpointResponse } from '@/types/endpoints';
import type { WriteRequestOptions } from '../../requestOptions';

export interface EnsureCommonalityFieldsRequest {
  commonality_ids: number[];
}

export interface CreatedCommonalityField {
  commonality_id: number;
  name: string;
  type: string;
}

export interface CommonalityFieldError {
  commonality_id: number;
  name: string;
  error: string;
}

export interface EnsureCommonalityFieldsResponse {
  created: CreatedCommonalityField[];
  errors: CommonalityFieldError[];
}

type EnsureCommonalityFieldsApiResponse = Partial<EnsureCommonalityFieldsResponse> & {
  data?: EnsureCommonalityFieldsResponse;
};

type RequestParams = {
  subscriptionId: number | string;
  commonalityIds: number[];
};

export const ensureCommonalityFields = async (
  scope: RequestScope,
  { subscriptionId, commonalityIds }: RequestParams,
  options: WriteRequestOptions
): Promise<EndpointResponse<EnsureCommonalityFieldsResponse>> => {
  try {
    if (!subscriptionId) {
      throw new Error('Subscription ID is required');
    }
    if (!commonalityIds || commonalityIds.length === 0) {
      throw new Error('At least one commonality ID is required');
    }

    const requestBody: EnsureCommonalityFieldsRequest = {
      commonality_ids: commonalityIds,
    };

    const result = await scopedChannelApi(scope).post<EnsureCommonalityFieldsApiResponse>('atts/commonality/ensure-product-fields', requestBody, {
      ...options,
      headers: buildAsturHeaders({ subscriptionId }),
    });
    // Normalize rather than reject: the write has already happened
    // server-side, so a backend that omits an empty `created`/`errors` array
    // must not surface a completed operation as a failure.
    const response = (result.data?.data ?? result.data) as Partial<EnsureCommonalityFieldsResponse> | null | undefined;
    if (!response || typeof response !== 'object') {
      throw new Error('Invalid ensure commonality fields response');
    }

    return {
      result: {
        created: Array.isArray(response.created) ? response.created : [],
        errors: Array.isArray(response.errors) ? response.errors : [],
      },
      status: result.status,
      execution_time: 0,
      num_results: 1,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
